import React from "react";
import "./Table.scss";
import NoData from "Pages/Admin/InspectorAssign/components/NoData";

interface Column<T> {
  header: React.ReactNode;
  accessor?: keyof T;
  render?: (row: T, index: number) => React.ReactNode;
  className?: string;
}

export type { Column };

interface GroupHeaderCell {
  label: React.ReactNode;
  colSpan: number;
  className?: string;
}

export type { GroupHeaderCell };

interface TableProps<T extends object> {
  data?: T[];
  columns?: Column<T>[];
  rowClassName?: (row: T, index: number) => string;
  onRowClick?: (row: T) => void;
  footerRow?: React.ReactNode[];
  tableClassName?: string;
  footerRowClassName?: string;
  fallbackUI?: React.ReactNode;
  spanRow?: (row: T, index: number) => React.ReactNode;
  // Optional extra header row rendered above the column-header row, for
  // tables that group several columns under a shared label (colspan-based).
  groupHeaderRow?: GroupHeaderCell[];
}

export default function Table<T extends object>({
  data = [],
  columns = [],
  rowClassName,
  onRowClick,
  footerRow,
  tableClassName,
  footerRowClassName,
  fallbackUI,
  spanRow,
  groupHeaderRow,
}: TableProps<T>) {
  return (
    <div className="plist table-responsive Common">
      <table className={tableClassName ?? "table"}>
        <thead>
          {groupHeaderRow && (
            <tr className="chdrGroup">
              {groupHeaderRow.map((cell, i) => (
                <th key={i} colSpan={cell.colSpan} className={cell.className}>
                  {cell.label}
                </th>
              ))}
            </tr>
          )}
          <tr className="chdr">
            {columns.map((col, i) => (
              <th key={i} className={col.className}>
                {col.header}
              </th>
            ))}
          </tr>
        </thead>

        <tbody>
          {data.length === 0 ? (
            <tr className="empty-row">
              <td colSpan={columns.length} className="emptyState">
                {fallbackUI ? fallbackUI : <NoData />}
              </td>
            </tr>
          ) : (
            data.map((row, i) => {
              const spanContent = spanRow ? spanRow(row, i) : undefined;
              return (
                <tr
                  key={i}
                  className={`prow ${rowClassName ? rowClassName(row, i) : ""}`.trim()}
                  onClick={onRowClick ? () => onRowClick(row) : undefined}
                  style={onRowClick ? { cursor: "pointer" } : undefined}
                >
                  {spanContent !== undefined ? (
                    <td colSpan={columns.length}>{spanContent}</td>
                  ) : (
                    columns.map((col, j) => (
                      <td key={j} className={col.className}>
                        {col.render
                          ? col.render(row, i)
                          : col.accessor != null
                            ? String(row[col.accessor] ?? "")
                            : null}
                      </td>
                    ))
                  )}
                </tr>
              );
            })
          )}
        </tbody>
        {footerRow && (
          <tfoot>
            <tr className={footerRowClassName ?? "tableFooter"}>
              {footerRow.map((cell, i) =>
                React.isValidElement(cell) && cell.type === "td" ? (
                  React.cloneElement(cell as React.ReactElement, { key: i })
                ) : (
                  <td key={i} className={columns[i]?.className}>
                    {cell}
                  </td>
                ),
              )}
            </tr>
          </tfoot>
        )}
      </table>
    </div>
  );
}
